const CELL_SIZE = 4;
const CELL_SPACING = 20;
const IDLE_OPACITY = 0.2;
const TWINKLE_OPACITY = 0.6;
const HOVER_OPACITY = 1;
const HOVER_RADIUS = 132;
const POINTER_LERP = 0.08;
const MAX_DPR = 2;

type CellType = 'filled' | 'outline' | 'empty';

interface Cell {
  x: number;
  y: number;
  type: CellType;
  twinklePhase: number;
  twinkleSpeed: number;
}

function cellRandom(column: number, row: number, salt: number): number {
  let hash =
    Math.imul(column + 1, 374761393) ^ Math.imul(row + 1, 668265263) ^ salt;
  hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967296;
}

function cellType(column: number, row: number): CellType {
  let distribution = cellRandom(column, row, 0);
  if (distribution < 0.3) return 'filled';
  if (distribution < 0.8) return 'outline';
  return 'empty';
}

function initializeIsotypeGrid(canvas: HTMLCanvasElement): void {
  let maybeContext = canvas.getContext('2d');
  if (!maybeContext) return;
  let context: CanvasRenderingContext2D = maybeContext;

  let cells: Cell[] = [];
  let width = 0;
  let height = 0;
  let dpr = 1;
  let gridColor = getComputedStyle(canvas).color;
  let targetPointerX = 0;
  let targetPointerY = 0;
  let displayPointerX = 0;
  let displayPointerY = 0;
  let hasPointerPosition = false;
  let pointerIsInside = false;
  let isVisible = false;
  let animationFrame = 0;
  let reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function drawCell(cell: Cell, opacity: number): void {
    if (cell.type === 'empty') return;
    context.globalAlpha = opacity;

    if (cell.type === 'filled') {
      context.fillRect(cell.x, cell.y, CELL_SIZE, CELL_SIZE);
      return;
    }

    context.strokeRect(cell.x, cell.y, CELL_SIZE, CELL_SIZE);
  }

  function drawFrame(now = performance.now()): void {
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.fillStyle = gridColor;
    context.strokeStyle = gridColor;
    context.lineWidth = 1;

    let opacityRange = TWINKLE_OPACITY - IDLE_OPACITY;
    let hasHover = pointerIsInside && hasPointerPosition;
    let radiusSquared = HOVER_RADIUS * HOVER_RADIUS;

    for (let cell of cells) {
      let wave = reducedMotion.matches
        ? 0
        : Math.max(0, Math.sin(now * cell.twinkleSpeed + cell.twinklePhase));
      let twinkle = wave * wave * wave * wave;
      let opacity = IDLE_OPACITY + twinkle * opacityRange;

      if (hasHover && cell.type !== 'empty') {
        let deltaX = cell.x + CELL_SIZE * 0.5 - displayPointerX;
        let deltaY = cell.y + CELL_SIZE * 0.5 - displayPointerY;
        let distanceSquared = deltaX * deltaX + deltaY * deltaY;

        if (distanceSquared < radiusSquared) {
          let influence = 1 - Math.sqrt(distanceSquared) / HOVER_RADIUS;
          influence = influence * influence * (3 - 2 * influence);
          opacity = Math.max(
            opacity,
            IDLE_OPACITY + influence * (HOVER_OPACITY - IDLE_OPACITY)
          );
        }
      }

      drawCell(cell, opacity);
    }

    context.globalAlpha = 1;
  }

  function resize(): void {
    let bounds = canvas.getBoundingClientRect();
    let nextWidth = Math.max(1, Math.round(bounds.width));
    let nextHeight = Math.max(1, Math.round(bounds.height));
    let nextDpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    if (nextWidth === width && nextHeight === height && nextDpr === dpr) return;

    width = nextWidth;
    height = nextHeight;
    dpr = nextDpr;
    gridColor = getComputedStyle(canvas).color;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    let columns = Math.max(1, Math.floor((width - CELL_SIZE) / CELL_SPACING) + 1);
    let rows = Math.max(1, Math.floor((height - CELL_SIZE) / CELL_SPACING) + 1);
    let originX = (width - ((columns - 1) * CELL_SPACING + CELL_SIZE)) * 0.5;
    let originY = (height - ((rows - 1) * CELL_SPACING + CELL_SIZE)) * 0.5;
    cells = new Array<Cell>(columns * rows);

    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        let index = row * columns + column;
        cells[index] = {
          x: originX + column * CELL_SPACING,
          y: originY + row * CELL_SPACING,
          type: cellType(column, row),
          twinklePhase: cellRandom(column, row, 19349663) * Math.PI * 2,
          twinkleSpeed: 0.0015 + cellRandom(column, row, 83492791) * 0.002,
        };
      }
    }

    drawFrame();
  }

  function animate(now: number): void {
    displayPointerX += (targetPointerX - displayPointerX) * POINTER_LERP;
    displayPointerY += (targetPointerY - displayPointerY) * POINTER_LERP;
    drawFrame(now);
    animationFrame = requestAnimationFrame(animate);
  }

  function stopAnimation(): void {
    if (animationFrame === 0) return;
    cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }

  function startAnimation(): void {
    if (animationFrame !== 0 || !isVisible || reducedMotion.matches) return;
    animationFrame = requestAnimationFrame(animate);
  }

  function handlePointerMove(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') return;

    let bounds = canvas.getBoundingClientRect();
    let x = event.clientX - bounds.left;
    let y = event.clientY - bounds.top;
    pointerIsInside = x >= 0 && x <= bounds.width && y >= 0 && y <= bounds.height;

    if (!pointerIsInside) {
      if (reducedMotion.matches) drawFrame();
      return;
    }

    targetPointerX = x;
    targetPointerY = y;
    if (!hasPointerPosition || reducedMotion.matches) {
      displayPointerX = x;
      displayPointerY = y;
      hasPointerPosition = true;
    }
    if (reducedMotion.matches) drawFrame();
  }

  let resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(canvas);

  let themeObserver = new MutationObserver(() => {
    gridColor = getComputedStyle(canvas).color;
    drawFrame();
  });
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  let intersectionObserver = new IntersectionObserver((entries) => {
    isVisible = entries[0]?.isIntersecting ?? false;
    if (isVisible) {
      resize();
      startAnimation();
      return;
    }
    stopAnimation();
  });
  intersectionObserver.observe(canvas);

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      stopAnimation();
      drawFrame();
      return;
    }
    startAnimation();
  });

  window.addEventListener('pointermove', handlePointerMove, { passive: true });
  resize();
}

function initializeIsotypeGrids(): void {
  let canvases = document.querySelectorAll<HTMLCanvasElement>('[data-isotype-grid]');
  for (let canvas of canvases) initializeIsotypeGrid(canvas);
}

initializeIsotypeGrids();
